/**
 * Modal host for the medical PA three-dots menu (guidelines viewer, tx plan
 * images, NAR grid, DX codes, document merger).
 *
 * @module ThreeDotsModals
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { MutableRefObject } from "react";
import { useDispatch } from "react-redux";
import { AuthOnFile } from "../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../enums/eventsData";
import { ModalId } from "../../enums/modalId";
import { closeModal } from "../../redux/slice/modalSlice";
import { AppDispatch } from "../../redux/store/store";
import ClinicalDataTable from "../modals/clinicalDataTable/ClinicalDataTable";
import DocumentMerger from "../modals/documentMerger/documentMerger";
import DocViewerWithSideNavigation from "../modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import ImageviewerModal from "../modals/imageViewerModal/imageviewerModal";
import ImageViewerTimestamp from "../modals/imageViewerModal/imageViewerTimestamp";
import NarGridModal from "../modals/narGridModal/narGridModal";

/** Props for {@link ThreeDotsModals}. */
export interface ThreeDotsModalsProps {
  openedModalId: string | null;
  authOnFile: AuthOnFile | undefined;
  abortControllerRef: MutableRefObject<AbortController | null>;
  screenName: ScreenNamesForBigQuery;
}

/**
 * Component that renders all modals used by the ThreeDotsWithClickableOptions.
 * Separates modal rendering from the main component logic.
 */
const ThreeDotsModals: React.FC<ThreeDotsModalsProps> = ({
  openedModalId,
  authOnFile,
  abortControllerRef,
  screenName,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const handleCloseNarGridModal = () => {
    dispatch(closeModal());
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = null;
  };

  return (
    <>
      {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
        <DocViewerWithSideNavigation
          screenName={ScreenNamesForBigQuery.FORM_SCREEN}
          shouldLogEvent={true}
          modalName={ModalNamesForBigQuery.NCCN_GUIDELINES_MODAL}
        />
      )}

      {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
        <ImageviewerModal
          fileUrls={[]}
          onClose={handleCloseModal}
          filePaths={
            authOnFile?.screenshots?.treatment_plan_path
              ? [authOnFile?.screenshots?.treatment_plan_path ?? ""]
              : []
          }
        >
          <ImageViewerTimestamp createdAt={authOnFile?.created_at} />
        </ImageviewerModal>
      )}

      {openedModalId === ModalId.NAR_GRID_MODAL && (
        <NarGridModal onClose={handleCloseNarGridModal} />
      )}

      {openedModalId === ModalId.CLINICAL_DATA_TABLE && (
        <ClinicalDataTable
          screenName={screenName}
          data={{
            entries: [],
          }}
          onSave={() => {
            toast.success("New Primary ICD-10 code saved successfully!");
            // if (orderId && orgId) {
            //   fetchAll(orderId, orgId);
            // }
            dispatch(closeModal());
          }}
          onClose={() => dispatch(closeModal())}
        />
      )}

      {openedModalId === ModalId.DOCUMENT_MERGER_MODAL && <DocumentMerger />}
    </>
  );
};

export default ThreeDotsModals;
